<template>
	<AppLayout
		:title="`${dayjs(admissionElection.starts_at).format('DD MMM YY HH:mm')} - ${dayjs(admissionElection.ends_at).format('DD MMM YY HH:mm')}`"
		:return-url="route('association.representatives.admissions.index')"
		:return-label="trans('menu.association.representatives.admissions')"
	>
		<Modal v-if="canRatify && !admissionElection.ratified" id="upload-ratification-file-modal">
			<template #title>{{ trans('rotations.admissions.actions.ratify') }}</template>
			<LabeledObject>
				<template #label>{{ trans('rotations.admissions.ratification_file') }}</template>
				<input type="file" accept="application/pdf" @change.stop="loadFile">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="ratify">
					<i class="fa-solid fa-upload mr-2"/>
					{{ trans('general.upload_file') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<SecondaryButton :color="Color.Blue" @click.stop="copyCode(route('association.representatives.admissions.vote.index', admissionElection.id))">
				<i class="fa-solid fa-link mr-2"/>
				{{ trans('rotations.admissions.actions.copy_link') }}
			</SecondaryButton>
			<PrimaryButton v-if="canRatify && !admissionElection.ratified" :color="Color.Green" @click.stop="showModal('upload-ratification-file-modal')">
				<i class="fa-solid fa-file-upload mr-2"/>
				{{ trans('rotations.admissions.actions.ratify') }}
			</PrimaryButton>
		</template>
		<Alert v-if="admissionElection.ratified" :color="Color.Green">{{ trans('rotations.admissions.ratifications.ratified') }}</Alert>
		<Card v-for="rotation in rotations" :key="`rotation-${rotation.id}`" :title="rotation.college.acronym" :subtitle="rotation.college.name">
			<List>
				<ListItem v-for="admittedCandidate in rotation.admitted_candidates" :key="`admitted-candidate-${admittedCandidate.id}`" :title="admittedCandidate.user.full_name">
					<template #icon>
						<i class="fa-solid fa-circle-check text-green-600 dark:text-green-700"/>
					</template>
					<div v-if="votes[admittedCandidate.id]" class="flex items-center gap-6">
						<div class="text-xl text-green-600 dark:text-green-700 flex items-center gap-2">
							<p>{{ votes[admittedCandidate.id]['1'] }}</p>
							<i class="fa-solid fa-thumbs-up"/>
						</div>
						<div class="text-xl text-gray-400 dark:text-gray-700 flex items-center gap-2">
							<p>{{ votes[admittedCandidate.id]['abstains'] }}</p>
							<i class="fa-solid fa-face-meh-blank"/>
						</div>
						<div class="text-xl text-red-600 dark:text-red-700 flex items-center gap-2">
							<p>{{ votes[admittedCandidate.id]['0'] }}</p>
							<i class="fa-solid fa-thumbs-down"/>
						</div>
						<p class="text-gray-500">{{ transChoice('rotations.admissions.votes_count', votes[admittedCandidate.id]['total']) }}</p>
					</div>
					<p v-else class="text-gray-500">{{ trans('rotations.admissions.exceptions.no_votes') }}</p>
				</ListItem>
			</List>
			<List v-if="canRatify">
				<ListItem v-for="electedCandidate in rotation.elected_candidates" :key="`elected-candidate-${electedCandidate.id}`" :title="electedCandidate.user.full_name" @click.stop="toggleCandidate(electedCandidate.id)">
					<template #icon>
						<i v-if="candidateWillBeAdmitted(electedCandidate.id)" class="fa-solid fa-circle-check text-green-700"/>
						<i v-else class="fa-solid fa-circle-xmark text-red-700"/>
					</template>
					<div v-if="votes[electedCandidate.id]" class="flex items-center gap-6">
						<div class="text-xl text-green-600 dark:text-green-700 flex items-center gap-2">
							<p>{{ votes[electedCandidate.id]['1'] }}</p>
							<i class="fa-solid fa-thumbs-up"/>
						</div>
						<div class="text-xl text-gray-400 dark:text-gray-700 flex items-center gap-2">
							<p>{{ votes[electedCandidate.id]['abstains'] }}</p>
							<i class="fa-solid fa-face-meh-blank"/>
						</div>
						<div class="text-xl text-red-600 dark:text-red-700 flex items-center gap-2">
							<p>{{ votes[electedCandidate.id]['0'] }}</p>
							<i class="fa-solid fa-thumbs-down"/>
						</div>
						<p class="text-gray-500">{{ transChoice('rotations.admissions.votes_count', votes[electedCandidate.id]['total']) }}</p>
					</div>
					<p v-else class="text-gray-500">{{ trans('rotations.admissions.exceptions.no_votes') }}</p>
				</ListItem>
			</List>
			<Alert v-else-if="rotation.elected_candidates.length > 0" :color="Color.Yellow">{{ trans('enrollments.accreditation.no_permissions') }}</Alert>
		</Card>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {type Ref, ref} from 'vue'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Alert from '@/Components/UI/Alerts/Alert.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {trans, transChoice} from 'laravel-vue-i18n'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type AdmissionElection} from '@/Types/Elections/AdmissionElection'
import {copyTextToClipboard, showError, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

interface Vote {
	total:number,
	abstains:number,
	'0':number,
	'1':number,
}

interface Votes {
	[key:string]:Vote,
}

const props = defineProps<{
	votes:Votes,
	canRatify:boolean,
	admitted:Array<number>,
	rotations:Array<Rotation>,
	admissionElection:AdmissionElection,
}>()

const working:Ref<boolean> = ref(false)
const admitted:Ref<Array<number>> = ref(props.admitted)
const rotations:Ref<Array<Rotation>> = ref(props.rotations)
const admissionElection:Ref<AdmissionElection> = ref(props.admissionElection)

async function copyCode(code:string):Promise<void>{
	copyTextToClipboard(code).then(() => {
		showSuccess(trans('general.copied_to_clipboard'))
	})
}

function loadFile(event:Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		admissionElection.value.ratification_file = event.target.files[0]
	}
}

function candidateWillBeAdmitted(id:number){
	return admitted.value.includes(id)
}

function toggleCandidate(id:number){
	const index = admitted.value.findIndex((a:number) => a === id)
	if(index === -1){
		admitted.value.push(id)
	} else {
		admitted.value.splice(index, 1)
	}
}

function ratify():void{
	if(!admissionElection.value.ratification_file){
		showError(trans('rotations.admissions.exceptions.ratification_file_missing'))
	} else if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.updating'))
		const formData = new FormData
		formData.append('ratification_file', admissionElection.value.ratification_file)
		formData.append('admitted', JSON.stringify(admitted.value))
		axios.post(route('association.representatives.admissions.ratification.store', admissionElection.value.id), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(() => {
			showSuccess(trans('general.updated'))
			admissionElection.value.ratified = true
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>